import React, { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Accordion,        
  AccordionSummary, 
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { decryptOpenSSLAES } from "../uwin-decrypt";
import { getBeneficiaryAgeDisplay } from "../../../../helpers/common-uwinhelper";

/**
 * MemberCard Component
 * Displays beneficiary information in an expandable card format
 * Handles decryption of sensitive data when expanded
 */
const MemberCard = ({ beneficiaryData }) => {
  const [isCardExpanded, setIsCardExpanded] = useState(false);
  const [decryptedData, setDecryptedData] = useState({
    mobile: '',
    idType: '',
    idNumber: '',
    loading: false
  });

  /**
   * Decrypt sensitive beneficiary data
   * Called when card is expanded for the first time
   */
  const decryptCardData = async () => {
    setDecryptedData(prev => ({ ...prev, loading: true }));
    
    try {
      const mobile = beneficiaryData?.ben_mobile_number 
        ? decryptOpenSSLAES(beneficiaryData.ben_mobile_number)
        : 'Not Available';
      
      const idType = beneficiaryData?.photo_id_type 
        ? decryptOpenSSLAES(beneficiaryData.photo_id_type)
        : 'Not Available';
      
      const idNumber = beneficiaryData?.photo_id_number 
        ? decryptOpenSSLAES(beneficiaryData.photo_id_number)
        : 'Not Available';
      
      // Mask ID number (show last 4 chars)
      const maskedIdNumber = idNumber !== 'Not Available' 
        ? `******${idNumber.slice(-4)}` 
        : 'Not Available';
      
      setDecryptedData({
        mobile,
        idType,
        idNumber: maskedIdNumber,
        loading: false
      });
    } catch (error) {
      console.error('Error decrypting data:', error);
      setDecryptedData({
        mobile: 'Error',
        idType: 'Error',
        idNumber: 'Error',
        loading: false
      });
    }
  };

  /**
   * Handle card expansion
   * Decrypt data on first expansion
   */
  const handleCardToggle = () => {
    if (!isCardExpanded && !decryptedData.mobile) {
      decryptCardData();
    }
    setIsCardExpanded(!isCardExpanded);
  };

  return (
    <Accordion 
      expanded={isCardExpanded} 
      onChange={handleCardToggle}
      sx={{
        mt: 2,
        borderRadius: 1,
        background: "#F6F7F9",
        border: "1px solid #E0E0E0",
        boxShadow: "none",
        "&:before": { display: "none" }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ 
          minHeight: "auto",
          "& .MuiAccordionSummary-content": { 
            margin: "12px 0",
            "&.Mui-expanded": { margin: "12px 0" }
          }
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Typography fontWeight={600}>
            {beneficiaryData?.ben_name || "Name not available"}
          </Typography>
          <Typography fontSize={13}>
            {beneficiaryData?.beneficiary_gender || "Not specified"} &nbsp; | &nbsp; 
            {getBeneficiaryAgeDisplay(beneficiaryData)} &nbsp; | &nbsp; 
            {beneficiaryData?.dob || "DOB not available"}
          </Typography>
        </Box>
      </AccordionSummary>
      
      <AccordionDetails sx={{ pt: 0, pb: 2 }}>
        {decryptedData.loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={16} />
            <Typography fontSize={13}>Loading details...</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography fontSize={13} color="text.secondary">MOBILE</Typography>
              <Typography fontSize={13} fontWeight={500}>{decryptedData?.mobile}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography fontSize={13} color="text.secondary">IDENTIFICATION TYPE</Typography>
              <Typography fontSize={13} fontWeight={500}>{decryptedData?.idType}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography fontSize={13} color="text.secondary">IDENTIFICATION NO</Typography>
              <Typography fontSize={13} fontWeight={500}>{decryptedData?.idNumber}</Typography>
            </Box>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default MemberCard;